import { Dialog } from 'primereact/dialog';
import { RadioButton } from 'primereact/radiobutton';
import { Checkbox } from 'primereact/checkbox';
import { useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { formatNumber } from '../../assets/common';
import { useConfirmDialog } from '../../utils/ConfirmDialog/ConfirmDialogProvider';
import InputText from '../../components/InputText';

const PaymentDialog = ({ visible, onHide, paymentData, onConfirm }) => {
    const [paymentType, setPaymentType] = useState('');
    const [latePaymentInterest, setLatePaymentInterest] = useState(0);
    const [earlyPaymentDiscount, setEarlyPaymentDiscount] = useState(0);
    const [amountPaid, setAmountPaid] = useState(0);
    const [manualOverride, setManualOverride] = useState(false);
    const [paymentStatus, setPaymentStatus] = useState('');
    const [daysEarlyOrLate, setDaysEarlyOrLate] = useState(0);

    const { showDialog } = useConfirmDialog();

    const resetForm = () => {
        setPaymentType('');
        setLatePaymentInterest(0);
        setEarlyPaymentDiscount(0);
        setAmountPaid(0);
        setManualOverride(false);
        setPaymentStatus('');
        setDaysEarlyOrLate(0);
    };

    const handleHide = () => {
        resetForm();
        onHide();
    };

    useEffect(() => {
        if (!paymentData || !paymentData.installmentDate) return;

        const installmentDate = new Date(paymentData.installmentDate);
        installmentDate.setHours(0, 0, 0, 0);

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const diffTime = installmentDate - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        setDaysEarlyOrLate(Math.abs(diffDays));

        let status;
        if (diffDays > 0) {
            status = 'early';
            setPaymentType('early');
        } else if (diffDays < 0) {
            status = 'late';
            setPaymentType('late');
        } else {
            status = 'ontime';
            setPaymentType('regular');
        }

        setPaymentStatus(status);
    }, [paymentData, visible]);

    useEffect(() => {
        if (!paymentData) return;

        const installmentAmount = paymentData.totalAmountOfInstallments || 0;
        const installmentInterestAmount = paymentData.installmentInterestAmount || 0;

        let newLateInterest = 0;
        let newDiscount = 0;
        let newAmount = installmentAmount;

        if (paymentType === 'early' && !manualOverride) {
            const dailyInterest = installmentInterestAmount / 30;
            newDiscount = Math.min(dailyInterest * daysEarlyOrLate, installmentInterestAmount);
            newAmount = installmentAmount - newDiscount;
        } else if (paymentType === 'late' && !manualOverride) {
            const dailyInterest = installmentInterestAmount / 30;
            newLateInterest = dailyInterest * daysEarlyOrLate;
            newAmount = installmentAmount + newLateInterest;
        } else if (paymentType === 'regular') {
            newLateInterest = 0;
            newDiscount = 0;
            newAmount = installmentAmount;
        }

        setLatePaymentInterest(newLateInterest);
        setEarlyPaymentDiscount(newDiscount);
        setAmountPaid(newAmount);
    }, [paymentData, paymentType, daysEarlyOrLate, manualOverride]);

    useEffect(() => {
        if (!paymentData) return;

        const installmentAmount = paymentData.totalAmountOfInstallments || 0;
        const installmentInterestAmount = paymentData.installmentInterestAmount || 0;
        const principalAmount = installmentAmount - installmentInterestAmount;

        if (amountPaid < principalAmount) {
            setAmountPaid(principalAmount);

            // Adjust early payment discount if necessary
            if (earlyPaymentDiscount > installmentInterestAmount) {
                setEarlyPaymentDiscount(installmentInterestAmount);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [amountPaid, paymentData]);

    const handleConfirm = () => {
        onConfirm({
            id: paymentData?.id,
            amountPaid: parseFloat(amountPaid.toFixed(2)),
            latePaymentInterest: parseFloat(latePaymentInterest.toFixed(2)),
            interestRateCut: parseFloat(earlyPaymentDiscount.toFixed(2)),
            paymentType: paymentType
        });
        resetForm();
    };

    const getStatusMessage = () => {
        if (paymentStatus === 'early') {
            return `Taksit tarihinden ${daysEarlyOrLate} gün önce ödeme yapıyorsunuz. Erken ödeme indirimi uygulanabilir.`;
        } else if (paymentStatus === 'late') {
            return `Taksit tarihinden ${daysEarlyOrLate} gün sonra ödeme yapıyorsunuz. Gecikme faizi uygulanabilir.`;
        } else {
            return 'Taksit tarihinde ödeme yapıyorsunuz.';
        }
    };

    const handlePaymentTypeChange = (value) => {
        setPaymentType(value);
        setManualOverride(false);
        if (value === 'early') {
            setLatePaymentInterest(0);
        } else if (value === 'late') {
            setEarlyPaymentDiscount(0);
        } else if (value === 'regular') {
            setEarlyPaymentDiscount(0);
            setLatePaymentInterest(0);
        }
    };

    return (
        <Dialog
            header="Ödeme Tamamla"
            visible={visible}
            onHide={handleHide}
            style={{ maxHeight: "90vh", maxWidth: "80vw" }}
            footer={
                <div className="flex justify-between items-center gap-4 w-full h-full mt-4">
                    <Button
                        label="İptal"
                        icon="pi pi-times"
                        onClick={handleHide}
                        className="border rounded-xl shadow-lg cursor-pointer text-red-700 dark:text-red-400 hover:text-white dark:hover:text-white border-red-700 dark:border-red-500 hover:bg-red-800 dark:hover:bg-red-700 p-2"
                    />
                    <Button
                        label="Ödemeyi Tamamla"
                        icon="pi pi-check"
                        onClick={() => showDialog({
                            message: "Taksit ödemesini tamamlamak istediğinize emin misiniz ?",
                            onConfirm: () => {
                                handleConfirm();
                            }
                        })}
                        className="rounded-xl shadow-lg cursor-pointer text-white bg-green-700 dark:bg-green-800 hover:bg-green-800 dark:hover:bg-green-700 p-2"
                    />
                </div>
            }
            className="payment-dialog bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 border-0 shadow-lg"
            contentClassName="p-4 bg-white dark:bg-gray-900"
            headerClassName="bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700"
            footerClassName="bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end min-h-[60px]"
        >
            <div className="grid grid-cols-1 gap-4">
                <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                    <h3 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-100">Ödeme Bilgileri</h3>
                    <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-300">Taksit Tutarı:</span>
                        <span className="font-bold text-gray-800 dark:text-white">{paymentData?.totalAmountOfInstallments ? formatNumber(paymentData.totalAmountOfInstallments) + " ₺" : ''}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-300">Taksit Faiz Tutarı:</span>
                        <span className="font-bold text-gray-800 dark:text-white">{paymentData?.installmentInterestAmount ? formatNumber(paymentData.installmentInterestAmount) + " ₺" : ''}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-300">Ana Para:</span>
                        <span className="font-bold text-gray-800 dark:text-white">
                            {paymentData?.totalAmountOfInstallments && paymentData?.installmentInterestAmount
                                ? formatNumber(paymentData.totalAmountOfInstallments - paymentData.installmentInterestAmount) + " ₺"
                                : ''}
                        </span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-300">Taksit Tarihi:</span>
                        <span className="text-gray-800 dark:text-white">{paymentData?.installmentDate ? new Date(paymentData.installmentDate).toLocaleDateString('tr-TR') : ''}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-300">Bugünün Tarihi:</span>
                        <span className="text-gray-800 dark:text-white">{new Date().toLocaleDateString('tr-TR')}</span>
                    </div>
                    <div className="mt-3 p-3 bg-gray-100 dark:bg-gray-800 rounded-md">
                        <p className="text-sm text-gray-700 dark:text-gray-300">{getStatusMessage()}</p>
                    </div>
                </div>

                <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                    <h3 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-100">Ödeme Türü</h3>
                    <div className="flex flex-col gap-3">
                        {paymentStatus === 'early' && (
                            <div className="flex items-center">
                                <RadioButton
                                    inputId="early"
                                    name="paymentType"
                                    value="early"
                                    onChange={e => handlePaymentTypeChange(e.value)}
                                    checked={paymentType === 'early'}
                                    className="mr-2"
                                />
                                <label htmlFor="early" className="text-gray-700 dark:text-gray-300 ml-1">
                                    Erken Ödeme <span className="text-green-500 dark:text-green-400">(İndirim Uygulanır)</span>
                                </label>
                            </div>
                        )}
                        <div className="flex items-center">
                            <RadioButton
                                inputId="regular"
                                name="paymentType"
                                value="regular"
                                onChange={e => handlePaymentTypeChange(e.value)}
                                checked={paymentType === 'regular'}
                                className="mr-2"
                            />
                            <label htmlFor="regular" className="text-gray-700 dark:text-gray-300 ml-1">Normal Ödeme</label>
                        </div>
                        {paymentStatus === 'late' && (
                            <div className="flex items-center">
                                <RadioButton
                                    inputId="late"
                                    name="paymentType"
                                    value="late"
                                    onChange={e => handlePaymentTypeChange(e.value)}
                                    checked={paymentType === 'late'}
                                    className="mr-2"
                                />
                                <label htmlFor="late" className="text-gray-700 dark:text-gray-300 ml-1">
                                    Geç Ödeme <span className="text-red-500 dark:text-red-400">(Ek Faiz Uygulanır)</span>
                                </label>
                            </div>
                        )}
                    </div>
                </div>
                <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                    <div className="flex justify-between items-center mb-3">
                        <h3 className="text-lg font-semibold flex-grow text-gray-800 dark:text-gray-100">Ödeme Detayları</h3>
                        <div className="flex items-center">
                            <Checkbox
                                inputId="manualOverride"
                                checked={manualOverride}
                                onChange={e => setManualOverride(e.checked)}
                                className="mr-2"
                            />
                            <label htmlFor="manualOverride" className="text-sm text-gray-600 dark:text-gray-400 ml-1">Manuel Düzenleme</label>
                        </div>
                    </div>

                    {(paymentType === 'late' || (manualOverride && paymentType !== 'early')) && (
                        <div className="mb-3">
                            <InputText
                                name="latePaymentInterest"
                                type="number"
                                label="Gecikme Faizi"
                                value={latePaymentInterest}
                                onChange={(e) => {
                                    setManualOverride(true);
                                    const value = parseFloat(e.target.value) || 0;
                                    setLatePaymentInterest(value);
                                    setAmountPaid((paymentData?.totalAmountOfInstallments || 0) + value - earlyPaymentDiscount);
                                }}
                                clearFunction={() => {
                                    setLatePaymentInterest(0);
                                    setAmountPaid((paymentData?.totalAmountOfInstallments || 0) - earlyPaymentDiscount);
                                }}
                                min={0}
                                minFractionDigits={2}
                                maxFractionDigits={2}
                                currency="TRY"
                                disabled={!manualOverride && paymentType !== 'late'}
                            />
                        </div>
                    )}
                    {(paymentType === 'early' || (manualOverride && paymentType !== 'late')) && (
                        <div className="mb-3">
                            <InputText
                                name="earlyPaymentDiscount"
                                type="number"
                                label="Erken Ödeme İndirimi"
                                value={earlyPaymentDiscount}
                                onChange={(e) => {
                                    setManualOverride(true);
                                    const maxDiscount = paymentData?.installmentInterestAmount || 0;
                                    const discountValue = Math.min(parseFloat(e.target.value) || 0, maxDiscount);
                                    setEarlyPaymentDiscount(discountValue);
                                    setAmountPaid((paymentData?.totalAmountOfInstallments || 0) + latePaymentInterest - discountValue);
                                }}
                                clearFunction={() => {
                                    setEarlyPaymentDiscount(0);
                                    setAmountPaid((paymentData?.totalAmountOfInstallments || 0) + latePaymentInterest);
                                }}
                                min={0}
                                max={paymentData?.installmentInterestAmount || 0}
                                minFractionDigits={2}
                                maxFractionDigits={2}
                                currency="TRY"
                                disabled={!manualOverride && paymentType !== 'early'}
                            />
                        </div>
                    )}

                    <div className="my-6">
                        <InputText
                            name="amountPaid"
                            type="number"
                            label="Ödeme Yapılacak Tutar"
                            value={amountPaid}
                            onChange={(e) => {
                                setManualOverride(true);
                                const principalAmount = (paymentData?.totalAmountOfInstallments || 0) -
                                    (paymentData?.installmentInterestAmount || 0);
                                const newAmount = Math.max(parseFloat(e.target.value) || 0, principalAmount);
                                setAmountPaid(newAmount);
                            }}
                            clearFunction={() => {
                                const principalAmount = (paymentData?.totalAmountOfInstallments || 0) -
                                    (paymentData?.installmentInterestAmount || 0);
                                setAmountPaid(principalAmount);
                            }}
                            min={(paymentData?.totalAmountOfInstallments || 0) - (paymentData?.installmentInterestAmount || 0)}
                            minFractionDigits={2}
                            maxFractionDigits={2}
                            currency="TRY"
                        />
                    </div>
                </div>
                <div>
                    <h3 className="text-lg font-semibold mb-3 text-gray-800 dark:text-gray-100">Özet</h3>
                    <div className="flex justify-between mb-1">
                        <span className="text-gray-600 dark:text-gray-300">Taksit Tutarı:</span>
                        <span className="font-bold text-gray-800 dark:text-white">{paymentData?.totalAmountOfInstallments ? formatNumber(paymentData.totalAmountOfInstallments) + " ₺" : ''}</span>
                    </div>

                    {latePaymentInterest > 0 && (
                        <div className="flex justify-between mb-1">
                            <span className="text-gray-600 dark:text-gray-300">Gecikme Faizi:</span>
                            <span className="font-bold text-red-600 dark:text-red-400">{formatNumber(latePaymentInterest) + " ₺"}</span>
                        </div>
                    )}
                    {earlyPaymentDiscount > 0 && (
                        <div className="flex justify-between mb-1">
                            <span className="text-gray-600 dark:text-gray-300">Erken Ödeme İndirimi:</span>
                            <span className="font-bold text-green-600 dark:text-green-400">-{formatNumber(earlyPaymentDiscount) + " ₺"}</span>
                        </div>
                    )}
                    <div className="flex justify-between mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                        <span className="text-gray-700 dark:text-gray-300 font-semibold">Toplam Ödenecek Tutar:</span>
                        <span className="font-bold text-xl text-gray-800 dark:text-white">{formatNumber(amountPaid) + " ₺"}</span>
                    </div>
                </div>
            </div>
        </Dialog>
    );
};

export default PaymentDialog;